<template>
  <div
    v-if="!checkPermission('manage_settings') || forbidden"
    class="settings-css manage" >
    <h1
      class="settings__title w-100 flex" >
      <div
        class="back-btn"
        @click="goBack('WAManage')">
        <SBIcon
          class="mr-2"
          :icon="SBIconList.DOWN_ARROW"
          :color="SBColors.greyPrimary"
          :size="12"
          :rotate="90" />
        <p>Automated responses settings</p>
      </div>
    </h1>
    <Forbidden />
  </div>
  <div v-else>
    <div
      class="settings--limit-width settings-css manage" >

      <!-- Component title -->
      <h1
        class="settings__title w-100 flex" >
        <div
          class="back-btn"
          @click="goBack('WAManage')">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="12"
            :rotate="90" />
          <p>Automated responses settings</p>
        </div>
      </h1>

      <!-- Loading state of the component -->
      <Spinner
        v-if="!automatedResponseSettingsLoaded"
        class="mt-5"
        :thickness="2" />

      <!-- Initialized state of the component -->
      <div v-else>
        <ValidationObserver
          ref="automatedSettingsObserver"
          v-slot="{ valid, dirty, pristine }" >

          <!-- Wellcome message reply delay -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Welcome message reply delay
              <SBIcon
                id="welcome-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required">
              <multiselect
                v-model="automatedSettingsChanges.default_message_delay"
                placeholder="Select an option"
                class="flex-options hide-tags no-selected-background"
                :options="delays"
                label="text"
                track-by="value"
                :multiple="false"
                :show-labels="false"
                :show-no-results="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="false"
                :searchable="false" >
                <template
                  slot="option"
                  slot-scope="props">
                  <SBIcon
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9"/>
                  {{ props.option.text }}
                </template>
              </multiselect>
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>Required field</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="welcome-description"
              triggers="hover"
              placement="bottom" >
              Select how long to wait after a message is sent or received from a contact before sending another Welcome message if they contact you again.
            </b-popover>
          </b-form-group>

          <!-- Away message reply delay -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Away message reply delay
              <SBIcon
                id="away-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required">
              <multiselect
                v-model="automatedSettingsChanges.away_message_delay"
                placeholder="Select an option"
                class="flex-options hide-tags no-selected-background"
                :options="delays"
                label="text"
                track-by="value"
                :multiple="false"
                :show-labels="false"
                :show-no-results="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="false"
                :searchable="false" >
                <template
                  slot="option"
                  slot-scope="props">
                  <SBIcon
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9"/>
                  {{ props.option.text }}
                </template>
              </multiselect>
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>Required field</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="away-description"
              triggers="hover"
              placement="bottom" >
              Select how long to wait after a message is sent or received from a contact before sending another Away message if they contact you again.
            </b-popover>
          </b-form-group>

          <!-- Automated reply sequence duration -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Automated reply sequence duration
              <SBIcon
                id="sequence-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required">
              <multiselect
                v-model="automatedSettingsChanges.message_sequence_duration"
                placeholder="Select an option"
                class="flex-options hide-tags no-selected-background"
                :options="delays"
                label="text"
                track-by="value"
                :multiple="false"
                :show-labels="false"
                :show-no-results="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="false"
                :searchable="false" >
                <template
                  slot="option"
                  slot-scope="props">
                  <SBIcon
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9"/>
                  {{ props.option.text }}
                </template>
              </multiselect>
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>Required field</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="sequence-description"
              triggers="hover"
              placement="bottom" >
              Select for how long automated response flow should be active if contacts don’t finish the flow.
            </b-popover>
          </b-form-group>

          <!-- Human takeover duration -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Human takeover duration
              <SBIcon
                id="takeover-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required">
              <multiselect
                v-model="automatedSettingsChanges.in_touch_duration"
                placeholder="Select an option"
                class="flex-options hide-tags no-selected-background"
                :options="delays"
                label="text"
                track-by="value"
                :multiple="false"
                :show-labels="false"
                :show-no-results="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="false"
                :searchable="false" >
                <template
                  slot="option"
                  slot-scope="props">
                  <SBIcon
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9"/>
                  {{ props.option.text }}
                </template>
              </multiselect>
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>Required field</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="takeover-description"
              triggers="hover"
              placement="bottom" >
              Select for how long automated responses should remain turned off for a particular conversation after they were turned off.
              <span class="spacer" />
              Automated responses can be turned off by clicking on the lightning icon at the top of the conversation or by replying to a message via Inbox.
            </b-popover>
          </b-form-group>

          <!-- Turn bot Off after sending a WhatsApp Message Template -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Turn bot Off after sending a WhatsApp Message Template
              <SBIcon
                id="bot-off-after-template-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required">
              <multiselect
                v-model="automatedSettingsChanges.bot_on_after_wa_tmpl_msg_send"
                placeholder="Select an option"
                class="flex-options hide-tags no-selected-background"
                :options="yesNo"
                label="text"
                track-by="value"
                :multiple="false"
                :show-labels="false"
                :show-no-results="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="false"
                :searchable="false" >
                <template
                  slot="option"
                  slot-scope="props">
                  <SBIcon
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9"/>
                  {{ props.option.text }}
                </template>
              </multiselect>
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>Required field</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="bot-off-after-template-description"
              triggers="hover"
              placement="bottom" >
              When you send a WhatsApp Message Template from the inbox, bot will turn Off for that conversation.
            </b-popover>
          </b-form-group>

          <!-- Stop automated responses flow -->
          <b-form-group>
            <label
              class="settings__label-sub flex flex-align-center" >
              Stop automated responses flow
              <SBIcon
                id="stop-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              name="Keyword"
              rules="required|blockKeyword:stop,false">
              <b-input
                v-model="automatedSettingsChanges.stop_word"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter keyword" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <b-popover
              target="stop-description"
              triggers="hover"
              placement="bottom" >
              Keyword that contacts can send to stop the automated responses flow.
            </b-popover>
          </b-form-group>

          <div
            v-if="errorMessage"
            class="error-message flex center-all">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            {{ errorMessage }}
          </div>

          <FooterActions
            :form-valid="valid"
            :has-changes="dirty"
            :show-cancel="!pristine"
            :save-busy="saving"
            @reset="resetSettings"
            @save="updateAutomatedSettings" />
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

import ScrollArea from '../../../common/ScrollArea';
import Spinner from '../../../common/Spinner';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import FooterActions from '@/components/business/settings/partials/FooterActions';
import Forbidden from '@/components/errors/Forbidden';
import ToasterMixin from '@/mixins/Toaster';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import CommonActions from '@/components/manage/CommonActions.mixin';

export default {
  name: 'AutomatedResponsesPreferenceSettings',
  components: {
    ScrollArea,
    Spinner,
    Multiselect,
    SBIcon,
    FooterActions,
    Forbidden,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
    CommonActions,
  ],
  data() {
    return {
      automatedResponseSettingsLoaded: false,
      automatedSettingsChanges: {},
      settingsData: {},
      tempSavingState: null,
      saving: false,
      delays: [
        {
          text: '1 hour',
          value: '1hour',
        },
        {
          text: '12 hours',
          value: '12hours',
        },
        {
          text: '1 day',
          value: '1day',
        },
        {
          text: '1 week',
          value: '1week',
        },
      ],
      yesNo: [
        {
          text: 'Yes',
          value: false,
        },
        {
          text: 'No',
          value: true,
        },
      ],
      keysToFilterDelay: ['away_message_delay', 'default_message_delay', 'in_touch_duration', 'message_sequence_duration'],
      keysToFilterYesNo: ['bot_on_after_wa_tmpl_msg_send'],
      errorMessage: '',
      forbidden: false,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      userPermissions: 'user/userPermissions',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
  },
  watch: {
    business() {
      this.automatedResponseSettingsLoaded = false;
      this.tempSavingState = null;
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.fetchAutomatedResponseSettings(this.business.id).then(response => this.initialSettingsData(response.data));
      }
    },
    automatedSettingsChanges: {
      handler: function f() {
        if (this.testIfFormChanged() && this.$refs.automatedSettingsObserver) {
          this.$refs.automatedSettingsObserver.reset();
        }
      },
      deep: true,
    },
    userPermissions() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.fetchAutomatedResponseSettings(this.business.id).then(response => this.initialSettingsData(response.data));
      }
    },
  },
  mounted() {
    // in case user returns to the page. On first mount business is undefined so this will not trigger
    if (this.business.id) {
      this.fetchAutomatedResponseSettings(this.business.id).then(response => this.initialSettingsData(response.data));
    }
    window.eventBus.$on('403', this.accessDenied);
    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      fetchAutomatedResponseSettings: 'automation/fetchSettings',
      updateAutomatedResponseSettings: 'automation/updateSettings',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    initialSettingsData(settingsData) {
      this.automatedSettingsChanges = {};
      this.settingsData = settingsData;
      this.resetSettings();
      this.automatedResponseSettingsLoaded = true;
    },
    resetSettings() {
      this.errorMessage = '';
      const latestOriginalData = this.tempSavingState ? this.tempSavingState : this.settingsData;
      const permissions = Object.keys(latestOriginalData);
      permissions.forEach((permission) => {
        if (this.keysToFilterDelay.includes(permission)) {
          this.$set(this.automatedSettingsChanges, permission, {});
          const permissionRoleIndex = this.delays.findIndex(delay => delay.value === latestOriginalData[permission]);
          this.$set(this.automatedSettingsChanges, permission, this.delays[permissionRoleIndex]);
        } else if (this.keysToFilterYesNo.includes(permission)) {
          this.$set(this.automatedSettingsChanges, permission, {});
          const permissionRoleIndex = this.yesNo.findIndex(yn => yn.value === latestOriginalData[permission]);
          this.$set(this.automatedSettingsChanges, permission, this.yesNo[permissionRoleIndex]);
        } else {
          this.$set(this.automatedSettingsChanges, permission, latestOriginalData[permission]);
        }
      });
      if (this.$refs.customFieldObserver) {
        this.$refs.customFieldObserver.reset();
      }
    },
    normalizedChanges() {
      const normalizedChanges = {};
      const permissionsAvailableOnEdited = this.automatedSettingsChanges ? Object.keys(this.automatedSettingsChanges) : [];
      permissionsAvailableOnEdited.forEach((permission) => {
        if (this.keysToFilterDelay.includes(permission) || this.keysToFilterYesNo.includes(permission)) {
          normalizedChanges[permission] = this.automatedSettingsChanges[permission].value;
        } else {
          normalizedChanges[permission] = this.automatedSettingsChanges[permission];
        }
      });
      return normalizedChanges;
    },
    updateAutomatedSettings() {
      this.saving = true;
      this.errorMessage = '';
      const normalizedChanges = this.normalizedChanges();
      const payload = {
        merchant_id: this.business.id,
        data: { ...normalizedChanges },
      };

      this.updateAutomatedResponseSettings(payload)
        .then((response) => {
          if (response.status === 200) {
            this.tempSavingState = { ...normalizedChanges };
            this.resetSettings();
            this.showToaster('Automated responses settings have been successfully updated.', null, null, 'OK');
          }
        })
        .catch((error) => {
          this.errorMessage = (error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'Something went wrong! Please try again later.';
          // this.showToaster('Something went wrong. Please try again.', this.SBIconList.ERROR, 'danger');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    /** Test the change on the model with the last save data and disables the button if they are the same. */
    testIfFormChanged() {
      const latestOriginalData = this.tempSavingState ? this.tempSavingState : this.settingsData;
      const normalizedChanges = this.normalizedChanges();
      latestOriginalData.stop_word = latestOriginalData.stop_word.toLowerCase();
      normalizedChanges.stop_word = normalizedChanges.stop_word.toLowerCase();
      return this.$lodash.isEqual({ ...latestOriginalData }, { ...normalizedChanges });
    },
    accessDenied() {
      this.forbidden = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.spacer {
  display: block;
  height: 15px;
}

.settings__title {
  margin-bottom: 35px;
}
</style>
